/**
 * <s-logo-intro target="[data-header-logo]"> homepage brand entrance.
 * On load the large white logo sits high on the full-screen hero film, pinned to
 * the header logo position, with the header hidden. On scroll it stays in place,
 * shrinking and turning black into the header logo while the header fades in.
 * CSS (in the index template) reads --logo-intro-p and data-logo-intro-ready on <html>.
 * Reduced motion: no animation; the logo stays in the hero and the header shows.
 */
class SLogoIntro extends HTMLElement {
  connectedCallback() {
    this.root = document.documentElement;
    this.reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
    this.mark = this.querySelector('[data-logo-intro-mark]');
    this.target = document.querySelector(this.getAttribute('target') || '[data-header-logo]');
    if (!this.mark || !this.target) return this.disable();

    this.frame = 0;
    this.onScroll = () => {
      if (this.frame) return;
      this.frame = requestAnimationFrame(() => {
        this.frame = 0;
        this.update();
      });
    };
    this.onMotionChange = () => (this.reduced.matches ? this.disable() : this.enable());
    this.reduced.addEventListener('change', this.onMotionChange);
    this.onMotionChange();
  }

  disconnectedCallback() {
    this.disable();
    this.reduced?.removeEventListener('change', this.onMotionChange);
  }

  enable() {
    window.addEventListener('scroll', this.onScroll, { passive: true });
    window.addEventListener('resize', this.onScroll, { passive: true });
    this.update();
  }

  disable() {
    if (this.onScroll) {
      window.removeEventListener('scroll', this.onScroll);
      window.removeEventListener('resize', this.onScroll);
    }
    if (this.frame) cancelAnimationFrame(this.frame);
    this.frame = 0;
    this.root?.removeAttribute('data-logo-intro-ready');
    this.root?.style.removeProperty('--logo-intro-p');
    if (this.mark) {
      this.mark.style.transform = '';
      this.mark.style.top = '';
      this.mark.style.filter = '';
      this.mark.style.visibility = '';
    }
    if (this.target) this.target.style.visibility = '';
  }

  update() {
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const progress = Math.min(Math.max(window.scrollY / (vh * 0.55), 0), 1);
    const eased = 1 - Math.pow(1 - progress, 3);

    // The big logo is pinned to the header logo's top edge and only shrinks
    // (from its top-center) into place, so it stays put while scrolling.
    const startWidth = this.mark.offsetWidth || 1;
    const goal = this.target.getBoundingClientRect();
    const dx = (goal.left + goal.width / 2 - vw / 2) * eased;
    const scale = 1 + (goal.width / startWidth - 1) * eased;
    const done = progress >= 1;

    this.mark.style.top = `${goal.top}px`;
    this.mark.style.transform = `translateX(-50%) translateX(${dx}px) scale(${scale})`;
    this.mark.style.filter = `invert(${1 - eased})`;
    this.mark.style.visibility = done ? 'hidden' : '';
    this.target.style.visibility = done ? '' : 'hidden';
    this.root.style.setProperty('--logo-intro-p', eased.toFixed(3));
    this.root.toggleAttribute('data-logo-intro-ready', eased > 0.6);
  }
}

if (!customElements.get('s-logo-intro')) {
  customElements.define('s-logo-intro', SLogoIntro);
}
